import {
  pairHasOpenIssue,
  useTaskActivitiesForServiceRequest,
} from '@commenda/api/tasks';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useNotification } from '../../../hooks/useNotification';
import { ConfirmationModal } from '../../../molecules/modals/ConfirmationModal';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { updateTaskStatus } from '../../utils';
import { CaCorpIncorporationTask } from '../config';

export const MarkAsFiledComponent = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate: serviceRequestMutate } = context;

  const markAsFiledModalVisibility = useToggle();

  const { setNotification } = useNotification();

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );

  // Own task is agent-side; the SR-wide wait keys on the fill/review questionnaire pair.
  const fillTask = serviceRequest?.taskGroups
    ?.flatMap((taskGroup) => taskGroup.tasks)
    ?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        CaCorpIncorporationTask.FILL_CORPORATION_DETAILS
    );
  const reviewTask = serviceRequest?.taskGroups
    ?.flatMap((taskGroup) => taskGroup.tasks)
    ?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        CaCorpIncorporationTask.REVIEW_CORPORATION_DETAILS
    );
  const hasOpenReportedIssue = pairHasOpenIssue(
    activityFeed?.activities,
    fillTask,
    reviewTask
  );

  const onMarkAsFiled = async () => {
    const result = await updateTaskStatus({
      task,
      status: TaskStatus.COMPLETED,
      mutate: serviceRequestMutate,
    });
    if (!result) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to update the status',
        message: 'Please refresh and try again.',
      });
      throw new Error('Failed to mark as filed.');
    }
    markAsFiledModalVisibility.toggle();
    setNotification({
      show: true,
      valid: true,
      title: 'Status update successful',
      message: 'The incorporation has been successfuly marked as filed',
    });
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation filing completed by Commenda"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda files for incorporation" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda files for incorporation" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation filing completed"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={markAsFiledModalVisibility.toggle}
                variant="primary"
              >
                <Icons.Check className="size-4" /> Mark as filed
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="File for incorporation" />,
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Check className="size-4" /> Mark as filed
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="File for incorporation" />,
          },
        },
      };

      // Restored legacy ISSUE_REPORTED arm: SR-wide waiting state while the client fixes issues.
      if (
        hasOpenReportedIssue &&
        state.status !== TaskStatus.COMPLETED &&
        stateMap[state.role]
      ) {
        return {
          actions: null,
          label: (
            <ServiceRequestTaskLabel
              label="Client fixing the reported issues"
              status={task.status}
              timestamp={task.updatedAt}
            />
          ),
        };
      }

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <ConfirmationModal
        isOpen={markAsFiledModalVisibility.visible}
        onClose={markAsFiledModalVisibility.hide}
        onConfirm={onMarkAsFiled}
        title="Mark as filed"
        confirmButtonText="Continue"
        message="Please note that this action will update the customer. To revert this change, please contact Commenda support."
        hideCancelButton
      />
    </div>
  );
};
